import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

function PlatformCompliance() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [validating, setValidating] = useState(false);

  // Data state
  const [controls, setControls] = useState([]);
  const [latestValidation, setLatestValidation] = useState(null);
  const [drift, setDrift] = useState({ regressions_found: 0, drifts: [] });
  const [aiMetrics, setAiMetrics] = useState(null);

  // Filter state
  const [safeguardFilter, setSafeguardFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [controlsRes, validationRes, driftRes, aiRes] = await Promise.all([
        api.getComplianceControls(),
        api.getLatestValidation(),
        api.getComplianceDrift(),
        api.getAiGovernanceMetrics(),
      ]);

      setControls(controlsRes.controls || []);
      setLatestValidation(validationRes.validation || null);
      setDrift({
        regressions_found: driftRes.regressions_found || 0,
        drifts: driftRes.drifts || [],
      });
      setAiMetrics(aiRes.latest || null);
    } catch (err) {
      setError('Failed to load compliance data. Please try again.');
      console.error('PlatformCompliance loadData error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRunValidation = async () => {
    try {
      setValidating(true);
      const result = await api.triggerComplianceValidation();
      if (result.success) {
        // Reload all data after validation completes
        await loadData();
      }
    } catch (err) {
      setError('Validation failed: ' + (err.message || 'Unknown error'));
      console.error('triggerComplianceValidation error:', err);
    } finally {
      setValidating(false);
    }
  };

  // Compute summary from latest validation or controls
  const computeSummary = () => {
    if (latestValidation && latestValidation.summary) {
      return latestValidation.summary;
    }
    // Fall back to counting from controls
    let pass = 0;
    let fail = 0;
    let warning = 0;
    let total = 0;
    controls.forEach((c) => {
      if (c.latest_status) {
        total++;
        if (c.latest_status === 'PASS') pass++;
        else if (c.latest_status === 'FAIL') fail++;
        else if (c.latest_status === 'WARNING') warning++;
      }
    });
    const complianceRate = total > 0 ? Math.round((pass / total) * 100) : 0;
    return { compliance_rate: complianceRate, pass, fail, warning, total };
  };

  const summary = computeSummary();

  // Filter controls
  const filteredControls = controls.filter((c) => {
    if (safeguardFilter && c.safeguard_type !== safeguardFilter) return false;
    if (severityFilter && c.severity !== severityFilter) return false;
    return true;
  });

  const getStatusBadge = (status) => {
    if (!status) return <span className="badge" style={styles.badgeGray}>Not Run</span>;
    if (status === 'PASS') return <span className="badge success">PASS</span>;
    if (status === 'FAIL') return <span className="badge danger">FAIL</span>;
    if (status === 'WARNING') return <span className="badge warning">WARNING</span>;
    return <span className="badge">{status}</span>;
  };

  const formatTimestamp = (ts) => {
    if (!ts) return 'Never';
    return new Date(ts).toLocaleString();
  };

  if (loading) {
    return <div className="loading">Loading compliance data...</div>;
  }

  if (error && !controls.length) {
    return (
      <div className="card">
        <div style={styles.errorContainer}>
          <p style={styles.errorText}>{error}</p>
          <button className="button" onClick={loadData}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Platform Compliance</h2>
          <p style={styles.subtitle}>Monitor Newton Connect's HIPAA compliance posture</p>
        </div>
        <button
          className="button"
          onClick={handleRunValidation}
          disabled={validating}
        >
          {validating ? 'Running Validation...' : 'Run Validation'}
        </button>
      </div>

      {error && (
        <div style={styles.errorBanner}>
          {error}
        </div>
      )}

      {/* Latest Validation Summary */}
      <div style={styles.summaryRow}>
        <div className="card" style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Compliance Rate</div>
          <div style={styles.summaryValueLarge}>
            {summary.compliance_rate != null ? `${summary.compliance_rate}%` : '--'}
          </div>
        </div>
        <div className="card" style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Passed</div>
          <div style={styles.summaryValue}>
            <span className="badge success">{summary.pass != null ? summary.pass : 0}</span>
          </div>
        </div>
        <div className="card" style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Failed</div>
          <div style={styles.summaryValue}>
            <span className="badge danger">{summary.fail != null ? summary.fail : 0}</span>
          </div>
        </div>
        <div className="card" style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Warnings</div>
          <div style={styles.summaryValue}>
            <span className="badge warning">{summary.warning != null ? summary.warning : 0}</span>
          </div>
        </div>
        <div className="card" style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Last Run</div>
          <div style={styles.summaryTimestamp}>
            {latestValidation ? formatTimestamp(latestValidation.created_at || latestValidation.run_at) : 'Never'}
          </div>
        </div>
      </div>

      {/* Drift Detection */}
      {drift.regressions_found > 0 && (
        <div style={styles.driftWarningBanner}>
          <strong>Compliance Drift Detected:</strong> {drift.regressions_found} regression{drift.regressions_found !== 1 ? 's' : ''} found since last validation.
        </div>
      )}

      {drift.drifts && drift.drifts.length > 0 && (
        <div className="card" style={styles.sectionCard}>
          <h3 style={styles.sectionTitle}>Drift Detection</h3>
          <table className="table">
            <thead>
              <tr>
                <th>Control Code</th>
                <th>Previous Status</th>
                <th></th>
                <th>Current Status</th>
                <th>Severity</th>
              </tr>
            </thead>
            <tbody>
              {drift.drifts.map((d, i) => (
                <tr key={i}>
                  <td><strong>{d.control_code}</strong></td>
                  <td>{getStatusBadge(d.previous_status)}</td>
                  <td style={styles.arrowCell}>&rarr;</td>
                  <td>{getStatusBadge(d.current_status)}</td>
                  <td>
                    <span className={`badge ${
                      d.severity === 'critical' ? 'danger' :
                      d.severity === 'high' ? 'danger' :
                      d.severity === 'medium' ? 'warning' : 'info'
                    }`}>
                      {d.severity}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Controls Table */}
      <div className="card" style={styles.sectionCard}>
        <h3 style={styles.sectionTitle}>Compliance Controls</h3>
        <div style={styles.filtersRow}>
          <select
            value={safeguardFilter}
            onChange={(e) => setSafeguardFilter(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="">All Safeguard Types</option>
            <option value="TECHNICAL">TECHNICAL</option>
            <option value="ADMINISTRATIVE">ADMINISTRATIVE</option>
            <option value="AI_GOVERNANCE">AI_GOVERNANCE</option>
            <option value="PHYSICAL">PHYSICAL</option>
          </select>
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Control Code</th>
              <th>Title</th>
              <th>Safeguard Type</th>
              <th>Severity</th>
              <th>Latest Status</th>
              <th>Last Validated</th>
            </tr>
          </thead>
          <tbody>
            {filteredControls.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', color: '#666' }}>
                  No controls match the selected filters.
                </td>
              </tr>
            ) : (
              filteredControls.map((control) => (
                <tr key={control.id || control.control_code}>
                  <td><strong>{control.control_code}</strong></td>
                  <td>{control.control_name}</td>
                  <td>{control.safeguard_type}</td>
                  <td>
                    <span className={`badge ${
                      control.severity === 'critical' ? 'danger' :
                      control.severity === 'high' ? 'danger' :
                      control.severity === 'medium' ? 'warning' : 'info'
                    }`}>
                      {control.severity}
                    </span>
                  </td>
                  <td>{getStatusBadge(control.latest_status)}</td>
                  <td>{formatTimestamp(control.latest_validation_timestamp)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* AI Governance Section */}
      <div className="card" style={styles.sectionCard}>
        <h3 style={styles.sectionTitle}>AI Governance</h3>
        {aiMetrics ? (
          <div style={styles.aiMetricsGrid}>
            <div style={styles.aiMetricItem}>
              <div style={styles.aiMetricLabel}>Total Sessions</div>
              <div style={styles.aiMetricValue}>{aiMetrics.total_sessions != null ? aiMetrics.total_sessions : '--'}</div>
            </div>
            <div style={styles.aiMetricItem}>
              <div style={styles.aiMetricLabel}>Draft Sessions</div>
              <div style={styles.aiMetricValue}>{aiMetrics.draft_sessions != null ? aiMetrics.draft_sessions : '--'}</div>
            </div>
            <div style={styles.aiMetricItem}>
              <div style={styles.aiMetricLabel}>Approved Sessions</div>
              <div style={styles.aiMetricValue}>{aiMetrics.approved_sessions != null ? aiMetrics.approved_sessions : '--'}</div>
            </div>
            <div style={styles.aiMetricItem}>
              <div style={styles.aiMetricLabel}>Average Edit %</div>
              <div style={styles.aiMetricValue}>
                {aiMetrics.average_edit_percentage != null
                  ? `${Number(aiMetrics.average_edit_percentage).toFixed(1)}%`
                  : '--'}
              </div>
            </div>
          </div>
        ) : (
          <p style={styles.noDataText}>No AI governance metrics available.</p>
        )}
      </div>
    </div>
  );
}

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
  },
  title: {
    margin: '0 0 4px 0',
  },
  subtitle: {
    margin: 0,
    color: '#666',
    fontSize: '14px',
  },
  errorContainer: {
    textAlign: 'center',
    padding: '40px 20px',
  },
  errorText: {
    color: '#dc3545',
    marginBottom: '16px',
    fontSize: '16px',
  },
  errorBanner: {
    background: '#f8d7da',
    color: '#721c24',
    padding: '12px 16px',
    borderRadius: '6px',
    marginBottom: '20px',
    border: '1px solid #f5c6cb',
  },
  summaryRow: {
    display: 'flex',
    gap: '16px',
    marginBottom: '24px',
    flexWrap: 'wrap',
  },
  summaryCard: {
    flex: '1 1 160px',
    textAlign: 'center',
    padding: '20px 16px',
    minWidth: '140px',
  },
  summaryLabel: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '8px',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  summaryValueLarge: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#333',
  },
  summaryValue: {
    fontSize: '24px',
    fontWeight: '600',
  },
  summaryTimestamp: {
    fontSize: '14px',
    color: '#333',
    fontWeight: '500',
  },
  driftWarningBanner: {
    background: '#fff3cd',
    color: '#856404',
    padding: '14px 18px',
    borderRadius: '6px',
    marginBottom: '20px',
    border: '1px solid #ffc107',
    fontSize: '14px',
  },
  sectionCard: {
    marginBottom: '24px',
  },
  sectionTitle: {
    marginTop: 0,
    marginBottom: '16px',
  },
  filtersRow: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  filterSelect: {
    padding: '8px 12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    background: '#fff',
    minWidth: '180px',
  },
  arrowCell: {
    textAlign: 'center',
    fontSize: '18px',
    color: '#666',
    padding: '0 4px',
  },
  badgeGray: {
    background: '#6c757d',
    color: '#fff',
  },
  aiMetricsGrid: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
  },
  aiMetricItem: {
    flex: '1 1 180px',
    background: '#f8f9fa',
    padding: '20px',
    borderRadius: '8px',
    textAlign: 'center',
    minWidth: '150px',
  },
  aiMetricLabel: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '8px',
    fontWeight: '500',
  },
  aiMetricValue: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#333',
  },
  noDataText: {
    color: '#666',
    fontStyle: 'italic',
  },
};

export default PlatformCompliance;
